<template>
  <div class="org-tree-wrap">
    <div class="tree-scroll">
      <org-node :node="treeRoot" :is-root="true" />
    </div>
  </div>
</template>

<script>
import apis from '@/http/apis';

const OrgNode = {
  name: 'OrgNode',
  props: {
    node: { type: Object, required: true },
    isRoot: { type: Boolean, default: false },
  },
  data() { return { collapsed: false }; },
  render(h) {
    const node = this.node;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const vm = this;

    // -- 节点盒子 --
    const badge = node.cancelled
      ? h('span', { staticClass: 'otn-badge' }, ['\u6CE8\u9500'])
      : null;
    const nodeBox = h('div', {
      class: ['otn-box',
        this.isRoot && 'otn-box-root',
        node.cancelled && 'otn-box-cancelled'
      ].filter(Boolean),
    }, [badge, h('span', { staticClass: 'otn-name' }, [node.name])].filter(Boolean));

    // -- 折叠按钮 --
    const togConn = hasChildren
      ? h('div', {
          staticClass: 'otn-tog',
          on: { click() { vm.collapsed = !vm.collapsed; } },
        }, [h('span', { staticClass: 'otn-circle' }, [vm.collapsed ? '+' : '\u2212'])])
      : null;

    // -- 子节点行 --
    let childRow = null;
    if (hasChildren && !this.collapsed) {
      const branches = node.children.map((child, idx) => {
        const ratioEl = (child.equityRatio != null && Number(child.equityRatio) > 0)
          ? h('span', { staticClass: 'otn-ratio' }, [Number(child.equityRatio).toFixed(0) + '%'])
          : null;
        const arrow = h('span', { staticClass: 'otn-arrow' }, ['\u25BC']);
        const conn = h('div', { staticClass: 'otn-vconn' }, [ratioEl, arrow].filter(Boolean));
        const childNode = h(OrgNode, { key: idx, props: { node: child, isRoot: false } });
        return h('div', { key: idx, staticClass: 'otn-branch' }, [conn, childNode]);
      });
      childRow = h('div', { staticClass: 'otn-row' }, branches);
    }

    return h('div', { staticClass: 'otn-node' }, [nodeBox, togConn, childRow].filter(Boolean));
  },
};

function buildDefaultTree() {
  return {
    name: '枣庄市资产管理有限公司',
    cancelled: false, equityRatio: null,
    children: [
      {
        name: '枣庄市国资燃气投资有限公司',
        cancelled: false, equityRatio: 100,
        children: [
          { name: '枣庄市光明新型建材有限公司', cancelled: false, equityRatio: 50, children: [] },
          { name: '枣庄华润燃气有限责任公司', cancelled: false, equityRatio: 30, children: [] },
          { name: '枣庄市光明液化气有限责任公司', cancelled: false, equityRatio: 30, children: [] },
        ],
      },
      {
        name: '枣庄市国资金唐资产管理有限公司',
        cancelled: false, equityRatio: 40, children: [],
      },
    ],
  };
}

export default {
  name: 'ZCEquityOrgTree',
  components: { OrgNode },
  data() { return { treeRoot: buildDefaultTree() }; },
  mounted() {
    this.fetchData();
    this.$nextTick(() => { this._applyScale(); });
    window.addEventListener('resize', this._applyScale);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._applyScale);
  },
  watch: {
    treeRoot() {
      this.$nextTick(() => { this.$nextTick(() => { this._applyScale(); }); });
    },
  },
  methods: {
    _applyScale() {
      const wrap = this.$el;
      const scroll = wrap && wrap.querySelector('.tree-scroll');
      if (!wrap || !scroll) return;
      // 先重置 transform，获取原始尺寸
      scroll.style.transform = 'translate(-50%, -50%)';
      const wW = wrap.clientWidth - 12;
      const wH = wrap.clientHeight - 12;
      const sW = scroll.scrollWidth;
      const sH = scroll.scrollHeight;
      if (!sW || !sH || !wW || !wH) return;
      const s = Math.min(wW / sW, wH / sH, 3.5);
      scroll.style.transform = `translate(-50%, -50%) scale(${s.toFixed(6)})`;
      scroll.style.transformOrigin = 'center center';
    },
    fetchData() {
      apis.equityOrgTree()
        .then((res) => {
          console.log('[equity-tree] api res:', JSON.stringify(res && res.data));
          const root = res && res.data && res.data.root ? res.data.root : null;
          if (root && root.name) {
            // 只有当 API 数据有真正的層级深度时才使用（防止后端返回扁平数据覆盖默认树）
            const hasDepth = Array.isArray(root.children) && root.children.length > 0
              && root.children.some(c => Array.isArray(c.children) && c.children.length > 0);
            if (hasDepth) {
              console.log('[equity-tree] 使用API数据, 层级正确');
              this.treeRoot = root;
            } else {
              console.warn('[equity-tree] API数据层级扁平或不完整，保留默认树。root.children:', JSON.stringify(root.children));
            }
          }
        })
        .catch((err) => {
          console.error('[equity-tree] api 请求失败，使用默认树:', err);
        });
    },
  },
};
</script>

<!-- 全局样式（no scoped），类名 otn-* 前缀隔离 -->
<style>
.org-tree-wrap {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
}
.org-tree-wrap::-webkit-scrollbar { width: 4px; height: 4px; }
.org-tree-wrap::-webkit-scrollbar-thumb { background: rgba(33,118,255,0.4); border-radius: 2px; }

/* 缩放容器 */
.tree-scroll {
  position: absolute;
  top: 50%;
  left: 58%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  min-width: max-content;
}

/* 节点外层（列排列） */
.otn-node {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 节点盒子 */
.otn-box {
  position: relative;
  display: inline-block;
  min-width: 80px;
  max-width: 100px;
  padding: 4px 6px;
  border: 1.5px solid rgba(100, 200, 255, 0.9);
  border-radius: 4px;
  background: rgba(0, 20, 80, 0.82);
  color: #e8f4ff;
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
  word-break: break-word;
  box-shadow: 0 0 10px rgba(0, 150, 255, 0.35), inset 0 0 6px rgba(0, 100, 200, 0.2);
}
/* 根节点 — 渐变蓝 */
.otn-box.otn-box-root {
  min-width: 80px;
  max-width: none;
  white-space: nowrap;
  border-color: #59ccff;
  background: linear-gradient(135deg, #0058cc 0%, #0091ea 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 0 16px rgba(0, 180, 255, 0.6);
}
/* 注销节点 — 橙红边 */
.otn-box.otn-box-cancelled {
  border-color: #e55c2a;
  background: rgba(120, 30, 10, 0.7);
  color: #ffb89a;
}

/* 注销角标 */
.otn-badge {
  position: absolute;
  top: -9px;
  left: 50%;
  transform: translateX(-50%);
  background: #e55c2a;
  color: #fff;
  font-size: 11px;
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
}
.otn-name { display: block; }

/* 折叠按钮（圆圈 + 竖线） */
.otn-tog {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  cursor: pointer;
}
.otn-tog::before {
  content: '';
  position: absolute;
  left: 50%; top: 0;
  transform: translateX(-50%);
  width: 1.5px; height: 100%;
  background: rgba(80, 200, 255, 0.8);
}
.otn-circle {
  position: relative; z-index: 2;
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 1.5px solid rgba(80, 200, 255, 0.9);
  background: rgba(0, 40, 120, 0.9);
  color: #7de8ff;
  font-size: 13px;
  line-height: 13px;
  text-align: center;
  display: block;
  user-select: none;
}
.otn-circle:hover { background: rgba(0, 100, 220, 0.8); }

/* 子节点行（横向） */
.otn-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
}

/* 每个分支 — flex:1 1 0 使同级分支等宽，确保父节点始终居中于子节点之间 */
.otn-branch {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 70px;
  padding: 0 3px;
}
/* 横线 左半段 */
.otn-branch::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 50%;
  height: 1.5px;
  background: rgba(80, 200, 255, 0.8);
}
/* 横线 右半段 */
.otn-branch::after {
  content: '';
  position: absolute;
  top: 0; left: 50%; right: 0;
  height: 1.5px;
  background: rgba(80, 200, 255, 0.8);
}
.otn-branch:first-child::before { display: none; }
.otn-branch:last-child::after  { display: none; }
.otn-branch:only-child::before,
.otn-branch:only-child::after  { display: none; }

/* 分支竖线 + 比例 + 箭头 */
.otn-vconn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 22px;
  width: 100%;
}
.otn-vconn::before {
  content: '';
  position: absolute;
  top: 0; left: 50%;
  transform: translateX(-50%);
  width: 1.5px; height: 100%;
  background: rgba(80, 200, 255, 0.8);
}
.otn-ratio {
  position: relative; z-index: 1;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  background: transparent;
  padding: 0 2px;
  text-shadow: 0 0 6px rgba(255, 232, 77, 0.8);
}
.otn-arrow {
  position: relative; z-index: 1;
  color: #59ccff;
  font-size: 10px;
  line-height: 1;
}
</style>